'use client';

import type {
  IAnalyticsService,
  IAnalyticsServiceParams,
} from '../../analytics/AnalyticsService.types';
import type {
  IAnalytics,
  AnalyticsEvent,
} from '../../analytics/Analytics.types';
import { timeoutAfter } from '../../analytics/utils/timeoutAfter';
import { waitUntil } from '../../analytics/utils/waitUntil';
import type { Events } from '../../analytics';

// Type definitions for window.VWO
declare global {
  interface Window {
    VWO?: Array<unknown> & {
      state?: string;
      push: (args: unknown[]) => void;
      event?: (...args: unknown[]) => void;
    };
    _vwo_exp?: any;
  }
}

const eventsWhitelist: Set<Events> = new Set<Events>([
  'Form Viewed',
  'Form Step 1 Viewed',
  'Form Step 1 Initiated',
  'Form Step 1 Completed',
  'Form Completed',
]);

/**
 * VWO Service
 * @see https://developers.vwo.com/v2/docs/fme-javascript-metrics
 */
export const VWOService: IAnalyticsService = (
  params: IAnalyticsServiceParams = {}
): IAnalytics => {
  const { timeout = 2000 } = params;

  const _initializeVWOListeners = () => {
    window.VWO?.push([
      'onVWOLoaded',
      function () {
        console.debug('VWOService: VWO loaded');
      },
    ]);

    window.VWO?.push([
      'onVariationApplied',
      function (data: any) {
        const [_, experimentId, variationId] = data;
        if (
          typeof window._vwo_exp[experimentId].comb_n[variationId] !==
          'undefined'
        ) {
          const experiment = {
            campaignId: experimentId,
            campaignName: window._vwo_exp[experimentId].name,
            variationId: variationId,
            variationName: window._vwo_exp[experimentId].comb_n[variationId],
          };

          console.debug('VWOService: Variation applied', experiment);
        }
      },
    ]);

    window.VWO?.push([
      'onEventReceive',
      'rC',
      function (data: [any, any, any]) {
        const [_, experimentId, goalId] = data;
        console.debug(
          `VWOService: Goal ${goalId} triggered in Campaign ${experimentId}`
        );
      },
    ]);
  };

  _initializeVWOListeners();

  /**
   * Ensure that window.VWO.event is a function.
   *
   * vwo suggestion
   *
   * ```
   * 	window.VWO = window.VWO || [];
   * 	VWO.event = VWO.event || function () {VWO.push(["event"].concat([].slice.call(arguments)))};
   * ```
   * ...but this is conflicting with what vwo engineers tell
   *
   */
  const ensureVWOEvent = (): boolean => {
    if (typeof window === 'undefined') return false;
    if (!window.VWO) return false;

    return (
      window.VWO.state === 'executionComplete' &&
      typeof window.VWO.event === 'function'
    );
  };

  ensureVWOEvent();

  const isInitialized = () => typeof window !== 'undefined' && ensureVWOEvent();
  const waitForInitialization = () => waitUntil(isInitialized);

  /**
   *
   * @note The vwo preview mode shows the tracked event in the logs panel, with a few caveats:
   * - it is ONLY the event used as a goal in the a/b test
   * - and ONLY ONCE per user / variation (switch to another variation to reset and capture the events again)
   *
   * @howto Events need to be registered in the vwo dashboard first (data360 > events) before it can be used as a metric,
   * and added to the eventsWhitelist set above (non-registered events will throw an error when trying to track them).
   *
   */
  const track = async (e: AnalyticsEvent) => {
    const promise = new Promise<void>((resolve, reject) => {
      if (!eventsWhitelist.has(e.event as Events)) {
        console.debug(
          `VWOService: Event ${e.event} is not whitelisted. If you really need it, first add it to vwo and then add it to the eventsWhitelist set.`
        );
        return resolve();
      }

      if (isInitialized()) {
        const safe_event = e.event.replace(
          /(?:^\w|[A-Z]|\b\w|\s+)/g,
          (match, index) =>
            match.trim().toLowerCase() && index === 0
              ? match.toLowerCase()
              : match.trim().toUpperCase()
        );

        console.debug('VWOService: track', { safe_event });

        try {
          window.VWO?.event?.(safe_event);
          resolve();
        } catch (error) {
          console.error('Error using VWO for tracking:', error);
          reject(new Error('VWOService tracking failed'));
        }
      } else {
        reject(new Error('VWOService service is not present'));
      }
    });

    return timeoutAfter(promise, timeout, 'VWOService timed out');
  };

  return {
    name: 'vwo',
    waitForInitialization,
    isInitialized,
    track,
  };
};
